/**
 * Activation Landing Page
 * ========================
 * Landing page for restaurant owners who click activation link from sales rep.
 *
 * Flow:
 * 1. Extract token from URL (/activate/:token)
 * 2. Validate token via API
 * 3. Show restaurant info and OAuth login buttons
 * 4. Store session_token in localStorage before OAuth redirect
 * 5. Redirect to OAuth provider (Google, Facebook, Apple)
 *
 * After OAuth:
 * - OAuthCallback detects activation flow
 * - Routes to plan selection with session_token
 * - Plan selection passes session_token to Stripe checkout
 * - Webhook updates pre-created account with OAuth user_id
 *
 * Security:
 * - Token validated server-side before any action
 * - Checks expiration (7 days from invite_sent_at)
 * - Checks not already claimed
 *
 * Author: Claude Code
 * Date: 2025-11-13
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Loader2,
  AlertCircle,
  ArrowRight,
  Menu,
  Utensils,
  Sparkles,
  Shield
} from 'lucide-react';
import { useAuth } from '@/services/auth/authContext';
import { Checkbox } from '@/components/ui/checkbox';

interface ValidationResponse {
  success: boolean;
  restaurant_name: string;
  menu_item_count: number;
  rest_id: number;
  session_id: string;
  product_type?: string;
  message?: string;
}

const ActivationLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationData, setValidationData] = useState<ValidationResponse | null>(null);
  const [authLoading, setAuthLoading] = useState<string | null>(null);
  const [termsAndPrivacyAccepted, setTermsAndPrivacyAccepted] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);

  // Validate token on page load
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('No activation token provided');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/sales/validate-token/${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid or expired activation link');
      }

      const data: ValidationResponse = await response.json();
      setValidationData(data);
      setLoading(false);

      // Store session_token in localStorage for OAuth flow
      console.log('🔍 [ActivationLanding] API Response:', { session_id: data.session_id, rest_id: data.rest_id });

      // Validate session_id before storing
      if (data.session_id && data.session_id !== null && data.session_id !== 'null') {
        localStorage.setItem('activation_session_token', data.session_id);
        console.log('✅ [ActivationLanding] Stored activation_session_token:', data.session_id);
      } else {
        console.error('❌ [ActivationLanding] Invalid session_id from API:', data.session_id);
      }

      localStorage.setItem('activation_rest_id', String(data.rest_id));
      localStorage.setItem('activation_restaurant_name', data.restaurant_name);

      // Store product_type for PlanSelection.tsx (reads from 'selected_product_type')
      const productType = data.product_type || 'virtual_assistant';
      localStorage.setItem('selected_product_type', productType);
      console.log('✅ [ActivationLanding] Stored selected_product_type:', productType);

    } catch (err: any) {
      console.error('Token validation error:', err);
      setError(err.message || 'Failed to validate activation link');
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    // Validate terms acceptance before proceeding
    if (!termsAndPrivacyAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setAuthLoading(provider);

    try {
      // Store provider in case we need it after OAuth redirect
      localStorage.setItem('activation_oauth_provider', provider);

      // Store consent flags for later processing
      localStorage.setItem('activation_terms_accepted', 'true');
      localStorage.setItem('activation_privacy_accepted', 'true');
      localStorage.setItem('activation_email_opt_in', emailOptIn.toString());
      console.log('✅ [ActivationLanding] Stored consent flags:', {
        terms_accepted: true,
        privacy_accepted: true,
        email_opt_in: emailOptIn
      });

      // Initiate OAuth flow using authContext login
      await login(provider);

      // Note: User will be redirected away, so no need to clear loading state
    } catch (err: any) {
      console.error(`${provider} OAuth error:`, err);
      setError(`Failed to sign in with ${provider}. Please try again.`);
      setAuthLoading(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1f4ed1] via-[#2a5de0] to-[#1f4ed1] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-white animate-spin mx-auto" />
          <p className="mt-4 text-white text-lg">Validating your activation link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1f4ed1] via-[#2a5de0] to-[#1f4ed1] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 text-center"
        >
          <AlertCircle className="w-16 h-16 text-red-300 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold text-white">Invalid Activation Link</h1>
          <p className="mt-2 text-white/80">{error}</p>
          <p className="mt-4 text-white/60 text-sm">
            Please contact your sales representative for a new activation link.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-3 bg-[#FDB913] text-black font-medium rounded-lg hover:bg-[#FDB913]/90 transition-colors"
          >
            Go to Homepage
          </button>
        </motion.div>
      </div>
    );
  }

  // Success state - Show activation page
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1f4ed1] via-[#2a5de0] to-[#1f4ed1]">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full mb-4">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-green-300 font-medium">Activation Link Valid</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome to Dine AI!
            </h1>
            <p className="text-white/80 text-lg">
              Your restaurant demo is ready. Sign in to activate your account.
            </p>
          </div>

          {/* Restaurant Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#FDB913]/20 rounded-full flex items-center justify-center">
                <Utensils className="w-8 h-8 text-[#FDB913]" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white">
                  {validationData?.restaurant_name}
                </h2>
                <div className="flex items-center gap-4 mt-2 text-white/60">
                  <div className="flex items-center gap-2">
                    <Menu className="w-4 h-4" />
                    <span>{validationData?.menu_item_count} menu items</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <Sparkles className="w-6 h-6 text-[#FDB913] mb-2" />
                <h3 className="text-white font-medium mb-1">Menu Ready</h3>
                <p className="text-white/60 text-sm">Your menu is digitized and ready to go</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <Shield className="w-6 h-6 text-blue-400 mb-2" />
                <h3 className="text-white font-medium mb-1">AI-Powered</h3>
                <p className="text-white/60 text-sm">Smart recommendations enabled</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <h3 className="text-white font-medium mb-1">No Setup Needed</h3>
                <p className="text-white/60 text-sm">Everything is configured for you</p>
              </div>
            </div>
          </motion.div>

          {/* Sign In Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                Step 1 of 3: Sign In
              </h3>
              <p className="text-white/70">
                Choose your preferred sign-in method. You'll select a plan in the next step.
              </p>
            </div>

            {/* Legal Checkboxes */}
            <div className="space-y-3 mb-6 border-b border-white/20 pb-6">
              {/* Email Marketing Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="email-optin"
                  checked={emailOptIn}
                  onCheckedChange={(checked) => setEmailOptIn(checked as boolean)}
                  className="mt-0.5 border-white/40 data-[state=checked]:bg-[#FDB913] data-[state=checked]:border-[#FDB913]"
                />
                <label
                  htmlFor="email-optin"
                  className="text-sm text-white/70 cursor-pointer leading-relaxed"
                >
                  Yes, I'd like to receive tips, best practices, and promotional offers
                </label>
              </div>

              {/* Combined Terms and Privacy Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms-privacy"
                  checked={termsAndPrivacyAccepted}
                  onCheckedChange={(checked) => setTermsAndPrivacyAccepted(checked as boolean)}
                  className="mt-0.5 border-white/40 data-[state=checked]:bg-[#FDB913] data-[state=checked]:border-[#FDB913]"
                />
                <label
                  htmlFor="terms-privacy"
                  className="text-sm text-white/70 cursor-pointer leading-relaxed"
                >
                  I agree to Dine AI's{' '}
                  <a href="/terms" target="_blank" className="text-[#FDB913] hover:underline">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" className="text-[#FDB913] hover:underline">
                    Privacy Policy
                  </a>
                  <span className="text-red-400 ml-1">*</span>
                </label>
              </div>
            </div>

            {/* OAuth Buttons */}
            <div className="space-y-3">
              {/* Google */}
              <button
                onClick={() => handleOAuthLogin('google')}
                disabled={!!authLoading || !termsAndPrivacyAccepted}
                className="w-full py-4 px-6 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-lg transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading === 'google' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>Continue with Google</span>
              </button>

              {/* Apple */}
              <button
                onClick={() => handleOAuthLogin('apple')}
                disabled={!!authLoading || !termsAndPrivacyAccepted}
                className="w-full py-4 px-6 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading === 'apple' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                )}
                <span>Continue with Apple</span>
              </button>
            </div>

            {/* Security Notice */}
            <div className="mt-6 flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-400/20 rounded-lg">
              <Shield className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-white/80">
                <p className="font-medium text-white mb-1">Secure Sign-In</p>
                <p>
                  We use industry-standard OAuth authentication. Your credentials are never stored on our servers.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Next Steps Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-center"
          >
            <p className="text-white/60 text-sm mb-4">What happens next?</p>
            <div className="flex items-center justify-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">1</span>
                Sign In
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">2</span>
                Choose Plan
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">3</span>
                Dashboard
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ActivationLanding;
